import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Send, RefreshCw, Loader2, Trash2, Eye, Database, Clock, CheckCircle2, FileText, AlertTriangle } from 'lucide-react';
import { campaignsApi, CampaignRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Campaigns() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDebug, setErrorDebug] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    setErrorDebug(null);
    try {
      console.log('[Campaigns] Fetching from Creator via campaignsApi.list()...');
      const data = await campaignsApi.list();
      console.log('[Campaigns] Received', data.length, 'campaigns:', data);
      setCampaigns(data);
    } catch (err: any) {
      const message = err instanceof Error ? err.message : String(err);
      const debug = err?.debug || null;
      console.error('[Campaigns] Failed to fetch:', message, debug);
      setError(message);
      setErrorDebug(debug);
      toast.error(`Failed to load campaigns: ${message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCampaigns();
  }, []);
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await campaignsApi.delete(id);
      toast.success('Campaign deleted');
      setDeleteConfirm(null);
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      toast.error('Failed to delete campaign');
    } finally {
      setIsDeleting(false);
    }
  };
  // Filter by team and region
  const filteredCampaigns = campaigns.filter(c => {
    if (c.Team !== currentWorkspace.name) return false;
    if (currentSubRegion) {
      return !c.Region || c.Region === currentSubRegion;
    }
    return true;
  });
  // Debug: count how many were filtered out
  const filteredOutCount = campaigns.length - filteredCampaigns.length;
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Sent':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-1880">
            <CheckCircle2 className="h-3 w-3" data-id="element-1881" />
            Sent
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-1882">
            <Database className="h-3 w-3" data-id="element-1883" />
            Exported
          </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-1884">
            <FileText className="h-3 w-3" data-id="element-1885" />
            Draft
          </span>;
    }
  };
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  return <div className="space-y-6" data-id="element-1886">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1887">
        <div className="min-w-0" data-id="element-1888">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1889">
            Email Campaigns
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1890">
            Create audience buckets and export to Zoho Campaigns.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1891">
          <button onClick={fetchCampaigns} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-1892">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-1893" />
          </button>
          <Link to="/campaigns/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-1894">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-1895" />
            <span data-id="element-1896">Create Campaign</span>
          </Link>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-1897">
          <div className="flex items-start gap-3" data-id="element-1898">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1899" />
            <div className="flex-1 min-w-0" data-id="element-1900">
              <h3 className="text-sm font-medium text-red-800" data-id="element-1901">
                Failed to fetch campaigns from Creator
              </h3>
              <p className="mt-1 text-sm text-red-700 break-all" data-id="element-1902">{error}</p>
              {errorDebug && <details className="mt-2" data-id="element-1903">
                  <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800" data-id="element-1904">
                    Show debug info
                  </summary>
                  <pre className="mt-1 text-xs text-red-600 bg-red-100 rounded p-2 overflow-auto max-h-40 font-mono" data-id="element-1905">
                    {JSON.stringify(errorDebug, null, 2)}
                  </pre>
                </details>}
              <button onClick={fetchCampaigns} disabled={loading} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-md hover:bg-red-200 disabled:opacity-50" data-id="element-1906">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" data-id="element-1907" /> : <RefreshCw className="h-3 w-3" data-id="element-1908" />}
                Retry
              </button>
            </div>
          </div>
        </div>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1909">
        {loading && campaigns.length === 0 ? <div className="p-12 text-center" data-id="element-1910">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-1911" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-1912">Loading campaigns...</p>
          </div> : filteredCampaigns.length === 0 ? <div className="p-12 text-center" data-id="element-1913">
            <Send className="mx-auto h-12 w-12 text-gray-400" data-id="element-1914" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-1915">
              No campaigns found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1916">
              Start engaging your audience by creating a campaign.
            </p>
            <Link to="/campaigns/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1917">
              <Plus className="h-4 w-4" data-id="element-1918" />
              Create Campaign
            </Link>
          </div> : <div className="overflow-x-auto" data-id="element-1919">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1920">
              <thead className="bg-gray-50" data-id="element-1921">
                <tr data-id="element-1922">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1923">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1924">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1925">
                    Bucket ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1926">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1927">
                    Exported
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-1928">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1929">
                {filteredCampaigns.map(campaign => <tr key={campaign.ID} className="hover:bg-gray-50" data-id="element-1930">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1931">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[250px]" data-id="element-1932">
                        {campaign.Campaign_Name}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[250px]" data-id="element-1933">
                        {campaign.Email_Subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1934">
                      {getStatusBadge(campaign.Status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1935">
                      {campaign.Bucket_ID ? <code className="text-xs font-mono bg-purple-50 text-purple-700 px-2 py-1 rounded border border-purple-100" data-id="element-1936">
                          {campaign.Bucket_ID}
                        </code> : <span className="text-sm text-gray-400" data-id="element-1937">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-1938">
                      {campaign.Recipient_Count ? campaign.Recipient_Count.toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1939">
                      {formatDate(campaign.Exported_At)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1940">
                      <div className="flex items-center justify-end gap-2" data-id="element-1941">
                        <Link to={`/campaigns/${campaign.ID}`} className="text-gray-400 hover:text-blue-600 p-2 hover:bg-blue-50 rounded" title="View Details" data-id="element-1942">
                          <Eye className="h-4 w-4" data-id="element-1943" />
                        </Link>
                        <button onClick={() => setDeleteConfirm(campaign.ID)} className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-1944">
                          <Trash2 className="h-4 w-4" data-id="element-1945" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1946">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-1947">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-1948" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-1949">
              <div className="flex items-center gap-4 mb-4" data-id="element-1950">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-1951">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-1952" />
                </div>
                <div data-id="element-1953">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1954">
                    Delete Campaign
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-1955">
                    Are you sure? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-1956">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1957">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-1958">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-1959" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}